import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Theme, themes, defaultThemeId, ThemeTokens } from './themes';

const STORAGE_KEY = 'uas-theme';

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme.id);
  const tokens = theme.tokens as unknown as Record<string, string>;
  (Object.keys(tokens) as Array<keyof ThemeTokens>).forEach((key) => {
    root.style.setProperty(`--${camelToKebab(key)}`, tokens[key] as string);
  });
}

function camelToKebab(value: string): string {
  return value.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

interface ThemeContextValue {
  themeId: string;
  theme: Theme;
  themes: Theme[];
  setTheme: (id: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeId, setThemeId] = useState<string>(() => {
    if (typeof window === 'undefined') return defaultThemeId;
    return window.localStorage.getItem(STORAGE_KEY) || defaultThemeId;
  });

  const theme = themes[themeId] || themes[defaultThemeId];

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(STORAGE_KEY, theme.id);
  }, [theme]);

  const setTheme = (id: string) => {
    if (themes[id]) setThemeId(id);
  };

  return (
    <ThemeContext.Provider value={{ themeId, theme, themes: Object.values(themes), setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
